import { Listing } from '@smg-real-estate/hg-rets-schema';
import {
  executeSearch,
  type ListingItem,
  OfferType,
  RadiusOptions,
  SearchModel,
  type SearchResult,
} from '@smg-real-estate/hg-search-libs';
import { AxiosError } from 'axios';
import { uniqBy } from 'lodash-es';

import { getAcrHeaders, shouldInjectAcrHeaders } from '@/helpers/get-acr-headers';
import { ApiTypes, logErrorToDatadogCustomMetric, logInfo } from '@/helpers/LogHelper';
import {
  getListingWithPlatformSpecificMediaURLs,
  parsePlatformSpecificMediaURL,
} from '@/helpers/ParsePlatformSpecificMediaURL/ParsePlatformSpecificMediaURL';
import { captureError } from '@smg-real-estate/browser-rum';

import {
  DEFAULT_PRICE_INCREASE,
  relaxPriceCriteria,
  relaxRadiusCriteria,
} from '@/helpers/result-list.helpers';
import { HOMEGATE_AGENCY } from '@/resultlist/layoutSolutionRequest';
import type {
  ResultListActions,
  ResultListGetters,
  ResultListModule,
  ResultListMutations,
  ResultListState,
} from '@/store/types/result-list-state';
import {
  FallbackSearchType,
  type LayoutSolutionAgencyConfiguration,
  type LayoutSolutionRequest,
} from '@/types';

const isSSR = import.meta.env.SSR;

const initialState = (): ResultListState => ({
  search: {
    preSearch: {
      result: null,
      searchModel: null,
      dataFetchError: null,
      isLoading: false,
    },
    fullSearch: {
      result: null,
      searchModel: null,
      dataFetchError: null,
      isLoading: false,
    },
    fallbackSearch: {
      result: null,
      searchModel: null,
      dataFetchError: null,
      isLoading: false,
    },
  },
});

export function isPermanentError(error: AxiosError | Error | null): boolean {
  return !!(error as AxiosError<any>)?.response?.data?.message?.startsWith(
    'From must be between 0 and',
  );
}

export const getters: ResultListGetters = {
  preSearchResultCount: (resultListState) => {
    const search = resultListState.search;
    if (search.preSearch.isLoading) {
      return undefined;
    }
    return search.preSearch.result?.resultCount ?? search.fullSearch.result?.resultCount;
  },
  preSearchDataFetchError: (resultListState) => resultListState.search.preSearch.dataFetchError,
  preSearchIsLoading: (resultListState) => resultListState.search.preSearch.isLoading,
  fullSearchIsLoading: (resultListState) => resultListState.search.fullSearch.isLoading,
  fullSearchResult: (resultListState) => resultListState.search.fullSearch.result,
  fullSearchDataFetchError: (resultListState) => resultListState.search.fullSearch.dataFetchError,
  lastSearchModel: (resultListState) =>
    resultListState.search.fullSearch.searchModel
      ? SearchModel.deserialize(resultListState.search.fullSearch.searchModel)
      : null,
  fallbackSearchIsLoading: (resultListState) => resultListState.search.fallbackSearch.isLoading,
  fallbackSearchResult: (resultListState) => resultListState.search.fallbackSearch.result,
  fallbackSearchType: (resultListState) => {
    const originalSearch = resultListState.search.fullSearch;
    const fallbackSearch = resultListState.search.fallbackSearch;
    if (fallbackSearch.isLoading) {
      return undefined;
    }

    const originalRadius = originalSearch.searchModel?.radius ?? 0;

    if (fallbackSearch.searchModel?.radius === originalRadius + RadiusOptions.m10000) {
      return FallbackSearchType.RADIUS_10KM;
    }

    if (fallbackSearch.searchModel?.radius === originalRadius + RadiusOptions.m20000) {
      return FallbackSearchType.RADIUS_20KM;
    }

    const originalPrice = originalSearch.searchModel?.priceRange?.to ?? 0;

    if (
      fallbackSearch.searchModel?.offerType === OfferType.RENT &&
      fallbackSearch.searchModel?.priceRange?.to === originalPrice + DEFAULT_PRICE_INCREASE
    ) {
      return FallbackSearchType.PRICE_INCREASE;
    }

    return undefined;
  },
  fallbackSearchDataFetchError: (resultListState) =>
    resultListState.search.fallbackSearch.dataFetchError,
  uniqueVisibleListListings: (resultListState) => {
    const listings: Listing[] = [];

    const fullSearchListings = resultListState.search.fullSearch?.result?.listings ?? [];
    if (fullSearchListings.length > 0) {
      listings.push(
        ...(fullSearchListings.map((listingItem) => listingItem.listing) as ListingItem[]),
      );
    }

    const fallbackSearchListings = resultListState.search.fallbackSearch?.result?.listings ?? [];
    if (fallbackSearchListings.length > 0) {
      listings.push(
        ...(fallbackSearchListings.map((listingItem) => listingItem.listing) as ListingItem[]),
      );
    }

    return uniqBy(listings, 'id');
  },
};

export const mutations: ResultListMutations = {
  setResult: (resultListState, { result, name, error }) => {
    resultListState.search[name].result =
      name === 'fallbackSearch'
        ? deduplicateFallbackSearch(result, resultListState.search.fullSearch.result)
        : result;

    resultListState.search[name].dataFetchError = error;
  },
  setSearchModel: (resultListState, { searchModel, name }) =>
    (resultListState.search[name].searchModel = searchModel),
  setIsLoading: (resultListState, { isLoading, name }) =>
    (resultListState.search[name].isLoading = isLoading),
};

// The Axios errors contain a number of circular references causing the SSR to fail when trying to serialize
// the store for the subsequent client hydration. Here we only pick useful and serializable data from the errors.
function createErrorObject(error: any) {
  const errorObject: Record<string, any> = { message: error.message };
  if (error.response) {
    const { status, data, headers } = error.response;
    errorObject.response = { status, data, headers };
  }
  return errorObject;
}

function deduplicateFallbackSearch(
  fallbackResult: SearchResult | null,
  fullSearchResult: SearchResult | null,
): SearchResult | null {
  if (!fallbackResult?.listings?.length || !fullSearchResult?.listings?.length) {
    return fallbackResult;
  }

  const fullSearchListingIds = new Set(
    fullSearchResult.listings.map((listingItem) => listingItem.listing.id),
  );
  const deduplicatedListings = fallbackResult.listings.filter(
    (listingItem) => !fullSearchListingIds.has(listingItem.listing.id),
  );

  return {
    ...fallbackResult,
    resultCount: deduplicatedListings.length,
    listings: deduplicatedListings,
  };
}

export function prepareSearchModelForBackend(
  searchModel: SearchModel,
  layoutSolutionRequest: LayoutSolutionRequest | undefined,
  layoutSolutionAgencyConfiguration: LayoutSolutionAgencyConfiguration | null,
) {
  let searchModelForBackend = searchModel;

  if (searchModel.isCommercialSearch()) {
    searchModelForBackend.numberOfRooms = undefined;
  }

  if (layoutSolutionRequest) {
    let listers: string[] | undefined;

    if (layoutSolutionRequest.agency === HOMEGATE_AGENCY) {
      listers = undefined;
    } else if (layoutSolutionRequest.agency.includes(',')) {
      // Handle multiple agencies (comma-separated): a=heia,heiasub,x380
      // Use the provided list directly, ignore layoutSolutionAgencyConfiguration
      listers = layoutSolutionRequest.agency.split(',').map((id) => id.trim());
    } else if (layoutSolutionRequest.includeSubAgencies) {
      // Single agency with includeSubAgencies: use agency + sub-agencies from config
      listers = [
        layoutSolutionRequest.agency,
        ...(layoutSolutionAgencyConfiguration?.subAgencies ?? []),
      ];
    } else {
      // Single agency without includeSubAgencies
      listers = [layoutSolutionRequest.agency];
    }

    logInfo('Layout solution search request', {
      agency: layoutSolutionRequest.agency,
      includeSubAgencies: layoutSolutionRequest.includeSubAgencies,
      finalListers: listers,
      listerCount: listers?.length ?? 0,
    });

    searchModelForBackend = searchModelForBackend.withListers(listers);
  }
  return searchModelForBackend;
}

const actions: ResultListActions = {
  resetSearchResult: ({ commit }): void => {
    commit('setResult', { name: 'fullSearch', result: null, error: null });
    commit('setResult', { name: 'fallbackSearch', result: null, error: null });
  },
  doFlexibleSearch: async (
    { commit, rootGetters, dispatch },
    {
      searchModel,
      name = 'fullSearch',
      language,
      version,
      fieldset,
      layoutSolutionRequest,
      passThroughHeaders,
      authToken,
    },
  ): Promise<void> => {
    commit('setSearchModel', { name, searchModel: null });
    commit('setResult', { name, result: null, error: null });
    commit('setIsLoading', { name, isLoading: false });

    await dispatch('doSearch', {
      searchModel,
      name,
      language,
      version,
      fieldset,
      layoutSolutionRequest,
      passThroughHeaders,
      authToken,
    });
    const searchResult: SearchResult = rootGetters['resultList/fullSearchResult'];

    const countryGeoTagExists = searchModel.locations.some((location) =>
      location.startsWith('geo-country'),
    );

    if (countryGeoTagExists) {
      return;
    }

    if (searchResult?.resultCount <= 5) {
      try {
        await dispatch('doExtendedSearches', {
          searchModel,
          language,
          version,
          fieldset,
          layoutSolutionRequest,
          passThroughHeaders,
          authToken,
        });
      } catch (error) {
        captureError(error);
      } finally {
        commit('setIsLoading', { name: 'fallbackSearch', isLoading: false });
      }
    }

    if (!isSSR) {
      dispatch('fetchCustomPOI', {}, { root: true });

      dispatch(
        'loadCustomPOI',
        {
          listings: rootGetters['resultList/uniqueVisibleListListings'],
        },
        { root: true },
      );
    }
  },
  doExtendedSearches: async (
    { commit, rootGetters, dispatch },
    {
      searchModel,
      language,
      version,
      fieldset,
      layoutSolutionRequest,
      passThroughHeaders,
      authToken,
    },
  ): Promise<void> => {
    const relaxedSearchModels = [
      relaxRadiusCriteria(searchModel, RadiusOptions.m10000).withPageSize(20),
      relaxRadiusCriteria(searchModel, RadiusOptions.m20000).withPageSize(20),
    ];

    if (searchModel.offerType === OfferType.RENT && searchModel.priceRange?.to) {
      relaxedSearchModels.push(relaxPriceCriteria(searchModel, 500).withPageSize(20));
    }

    function hasFallbackResults(): boolean {
      const fallbackSearchResult = rootGetters['resultList/fallbackSearchResult'];
      return fallbackSearchResult?.resultCount > 0;
    }

    do {
      try {
        await dispatch('doSearch', {
          searchModel: relaxedSearchModels.shift(),
          name: 'fallbackSearch',
          language,
          version,
          fieldset,
          layoutSolutionRequest,
          passThroughHeaders,
          authToken,
        });
      } catch (error) {
        captureError(error);
      } finally {
        commit('setIsLoading', { name: 'fallbackSearch', isLoading: false });
      }
    } while (relaxedSearchModels.length > 0 && !hasFallbackResults());
  },
  // eslint-disable-next-line max-statements
  doSearch: async (
    { commit, rootState },
    {
      searchModel,
      name,
      version,
      fieldset,
      layoutSolutionRequest,
      passThroughHeaders,
      language,
      authToken,
    },
  ): Promise<void> => {
    if (!name) {
      throw new Error('name parameter for doSearch is required');
    }

    try {
      commit('setSearchModel', { name, searchModel });
      commit('setIsLoading', { name, isLoading: true });

      const countTotalHitsPrecisely = name === 'fullSearch';

      const layoutSolutionAgencyConfiguration = rootState.layoutSolution.agencyConfiguration;

      const searchModelForBackend = prepareSearchModelForBackend(
        searchModel,
        layoutSolutionRequest,
        layoutSolutionAgencyConfiguration,
      );

      let requestHeaders: Record<string, string> | null = null;

      // only add acrHeaders on client side and in production
      if (shouldInjectAcrHeaders()) {
        requestHeaders = { ...(await getAcrHeaders()) };
      }

      if (passThroughHeaders) {
        requestHeaders = { ...requestHeaders, ...passThroughHeaders };
      }

      if (language) {
        requestHeaders = { ...requestHeaders, 'Accept-Language': language };
      }

      if (authToken) {
        requestHeaders = { ...requestHeaders, Authorization: `Bearer ${authToken}` };
      }

      const requestConfig = {
        timeout: 5000,
        retryConfig: {
          retries: 3,
        },
        ...(requestHeaders && { headers: { ...requestHeaders } }),
      };

      const searchResult: SearchResult = await executeSearch(searchModelForBackend, {
        countTotalHitsPrecisely,
        version,
        requestConfig,
        fieldset,
      });

      // TODO: getListingWithPlatformSpecificMediaURLs and parsePlatformSpecificMediaURL are temporarily hack
      //  to cover https://homegate.atlassian.net/browse/RBD-1222,
      //  needs improvement...
      const searchResultsWithParsedMediaURLs = {
        ...searchResult,
        listings: searchResult.listings?.map((listingItem: ListingItem) => {
          return {
            ...listingItem,
            listing: getListingWithPlatformSpecificMediaURLs(listingItem.listing),
            listerBranding: {
              ...listingItem.listerBranding,
              logoUrl: parsePlatformSpecificMediaURL(listingItem.listerBranding?.logoUrl),
            },
          };
        }),
      };

      commit('setResult', {
        name,
        result: !searchResult.listings ? searchResult : searchResultsWithParsedMediaURLs,
        error: null,
      });
    } catch (error) {
      logErrorToDatadogCustomMetric(
        ApiTypes.search_api,
        { searchModel, searchName: name, passThroughHeaders },
        error,
      );

      commit('setResult', { name, result: null, error: createErrorObject(error) });
    } finally {
      commit('setIsLoading', { name, isLoading: false });
    }
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
} as ResultListModule;
